/**
 * TaskpaneApp.tsx — Main React app for the Office Add-in taskpane.
 *
 * Coordinates pages:
 *   1. LoginPage    — welcome screen + sign in (unauthenticated)
 *   2. AboutPage    — same welcome screen + go back
 *   3. HomePage     — profile selection + trigger styling
 *   4. ReviewPage   — paragraph review + style details summary
 *
 * The Toolbar is always visible at the top.
 * HomePage and ReviewPage are always mounted (inactive hidden via CSS)
 * so ReviewPage preserves paragraph position across navigation.
 */

import React, { useCallback, useEffect, useState } from "react";
import { Provider } from "react-redux";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { Button, FluentProvider, Spinner, webLightTheme } from "@fluentui/react-components";
import {
  initAuth,
  isAuthenticated,
  loginWithDialog,
  logout,
  onAuthFailure,
} from "src/auth/auth-client";
import { useAppDispatch, useAppSelector } from "src/hooks";
import { selectDocumentSummaryCounts } from "src/features/selectors";
import { setSelectedProfile } from "src/features/review-slice";
import { Api } from "src/api/api";
import { serverApi } from "src/api/server-api";
import { ErrorProvider, ProfileProvider, ReviewProvider, TaggingProvider } from "src/providers";
import {
  LAST_STYLING_RUN_SETTING_KEY,
  SELECTED_PROFILE_SETTING_KEY,
  loadDocumentSetting,
} from "src/services/settings-service";
import { getReactPlugin } from "src/services/telemetry";
import { store } from "src/store";
import { useTelemetryInit } from "src/taskpane/use-telemetry-init";
import { Toolbar } from "../components/Toolbar";
import { WelcomePage } from "../components/WelcomePage";
import { HomePage, ReviewPage } from "src/pages";
import { StyleHistoryProvider } from "src/providers";
import "./taskpane.css";

import type { ClassificationOutcome } from "src/providers";
import type { RootState } from "../store";

type Page = "styling" | "review";

interface TaskpaneContentProps {
  onSignOut?: () => void;
}

const TaskpaneContent: React.FC<TaskpaneContentProps> = ({ onSignOut }) => {
  // Mounting is the auth gate: this component only renders in the authenticated
  // branch below, and /api/config/client requires a valid Okta token.
  useTelemetryInit();

  const dispatch = useAppDispatch();
  const selectedProfile = useAppSelector((state: RootState) => state.review.selectedProfile);
  const summaryCounts = useAppSelector(selectDocumentSummaryCounts);
  const [currentPage, setCurrentPage] = useState<Page>("styling");
  const [showAbout, setShowAbout] = useState(false);
  const [isRestoringProfile, setIsRestoringProfile] = useState(true);

  // On load, check whether a profile was already saved into this document
  // (via document settings) — if so, jump straight to the Review page
  // instead of showing profile selection again. A saved profile only means the
  // user picked one, though: it is persisted before the styling run and also by
  // the Review Styles path, so a failed run must not land them on Review.
  useEffect(() => {
    async function restoreSavedProfile() {
      const savedProfileName = loadDocumentSetting<string>(SELECTED_PROFILE_SETTING_KEY);
      if (!savedProfileName) {
        setIsRestoringProfile(false);
        return;
      }

      try {
        await dispatch(serverApi.endpoints.getAvailableProfiles.initiate()).unwrap();
        dispatch(setSelectedProfile(savedProfileName));
        await Promise.all([
          dispatch(serverApi.endpoints.getFlattenedStyles.initiate(savedProfileName)).unwrap(),
          // Chapter mode groups paragraphs by the profile's book_section
          // nodes; without it every paragraph collapses into Front Matter.
          dispatch(serverApi.endpoints.getStyleProfile.initiate(savedProfileName)).unwrap(),
        ]);
        // Anything other than a recorded failure navigates: an absent outcome
        // covers the Review Styles path (which never runs styling) and
        // documents styled before this key existed.
        const lastRun = loadDocumentSetting<ClassificationOutcome>(LAST_STYLING_RUN_SETTING_KEY);
        if (lastRun !== "error") {
          setCurrentPage("review");
        }
      } catch (err) {
        console.warn("Failed to restore saved profile:", err);
      } finally {
        setIsRestoringProfile(false);
      }
    }

    restoreSavedProfile();
  }, [dispatch]);

  const handleStylingComplete = () => setCurrentPage("review");

  if (isRestoringProfile) {
    return (
      <>
        <Toolbar showAbout={false} />
        <div className="taskpane-body taskpane-body-loading">
          <Spinner size="small" label="Loading..." />
        </div>
      </>
    );
  }

  if (showAbout) {
    return (
      <>
        <Toolbar showAbout={false} />
        <WelcomePage actionLabel="Go Back" onAction={() => setShowAbout(false)} />
      </>
    );
  }

  return (
    <>
      <Toolbar onAbout={() => setShowAbout(true)} />
      <main id="app-body" className="taskpane-body">
        {currentPage === "styling" ? (
          <ProfileProvider>
            <HomePage
              onNavigateToReview={() => setCurrentPage("review")}
              onStylingComplete={handleStylingComplete}
            />
          </ProfileProvider>
        ) : (
          <ReviewProvider>
            <ReviewPage
              key={selectedProfile?.name}
              onNavigateToStyling={() => setCurrentPage("styling")}
            />
          </ReviewProvider>
        )}
        <div className="taskpane-footer">
          {currentPage === "review" && summaryCounts.paragraphCount > 0 && (
            <span className="taskpane-footer-summary">
              <span className="taskpane-footer-summary-part">
                {summaryCounts.styleCount} styles
              </span>{" "}
              across{" "}
              <span className="taskpane-footer-summary-part">
                {summaryCounts.categoryCount} categories,
              </span>{" "}
              <span className="taskpane-footer-summary-part">
                {summaryCounts.paragraphCount.toLocaleString()} paragraphs
              </span>
            </span>
          )}
          {onSignOut && (
            <Button appearance="transparent" size="small" onClick={onSignOut}>
              Sign Out
            </Button>
          )}
        </div>
      </main>
    </>
  );
};

export const TaskpaneApp: React.FC = () => {
  // `null` while the stored token is being validated/renewed, so the
  // sign-in screen does not flash before a silent renewal succeeds.
  const [authed, setAuthed] = useState<boolean | null>(__MSS_AUTH_DISABLED__ ? true : null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    initAuth();
    const unsubscribe = onAuthFailure(() => setAuthed(false));
    isAuthenticated()
      .then(setAuthed)
      .catch((err: unknown) => {
        console.error("[auth] startup check:", err);
        setAuthed(false);
      });
    return unsubscribe;
  }, []);

  const handleLogin = useCallback(async () => {
    setLoginLoading(true);
    setLoginError(null);
    try {
      await loginWithDialog();
      // Drop cache entries that failed with the expired token, otherwise
      // remounting resubscribes to the stale error instead of refetching.
      store.dispatch(Api.util.resetApiState());
      setAuthed(true);
    } catch (err: unknown) {
      setLoginError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoginLoading(false);
    }
  }, []);

  const handleLogout = useCallback(async () => {
    await logout();
    setAuthed(false);
  }, []);

  return (
    <Provider store={store}>
      <FluentProvider theme={webLightTheme}>
        <div className="taskpane-root">
          {!authed ? (
            <>
              <Toolbar showAbout={false} showVersion={false} />
              <WelcomePage
                actionLabel="Sign In"
                onAction={handleLogin}
                loading={loginLoading}
                error={loginError}
              />
            </>
          ) : (
            <AppInsightsContext.Provider value={getReactPlugin()}>
              <ErrorProvider>
                <TaggingProvider>
                  <ProfileProvider>
                    <StyleHistoryProvider>
                      <TaskpaneContent
                        onSignOut={!__MSS_AUTH_DISABLED__ ? handleLogout : undefined}
                      />
                    </StyleHistoryProvider>
                  </ProfileProvider>
                </TaggingProvider>
              </ErrorProvider>
            </AppInsightsContext.Provider>
          )}
        </div>
      </FluentProvider>
    </Provider>
  );
};
